import React from 'react';

import {
    DistanceNumber,
    PropertyList,
    PropertyListItem,
    SignedNumber,
} from '@dndbeyond/character-common-components/es';
import {
    Collapsible,
    DamageTypeIcon,
    MarketplaceCta,
    HtmlContent,
    AoeTypeIcon,
    ComponentConstants,
} from '@dndbeyond/character-components/es';
import {
    AbilityLookup,
    AccessUtils,
    Action,
    ActionUtils,
    ActivationUtils,
    BaseInventoryContract,
    CharacterTheme,
    Constants,
    DiceUtils,
    EntityUtils,
    EntityValueLookup,
    FormatUtils,
    HelperUtils,
    InfusionUtils,
    InventoryLookup,
    ItemUtils,
    LimitedUseUtils,
    ModelInfoContract,
    RuleData,
    RuleDataUtils,
    ValueUtils,
} from '@dndbeyond/character-rules-engine/es';

import EditorBox from '../EditorBox';
import SlotManager from '../SlotManager';
import SlotManagerLarge from '../SlotManagerLarge';
import ValueEditor from '../ValueEditor';
import { RemoveButton } from '../common/Button';

interface Props {
    action: Action;
    ruleData: RuleData;
    onCustomDataUpdate?: (propertyKey: Constants.AdjustmentTypeEnum, value: any, source: any) => void;
    showCustomize: boolean;
    entityValueLookup: EntityValueLookup;
    abilityLookup: AbilityLookup;
    inventoryLookup: InventoryLookup;
    isReadonly: boolean;
    largePoolMinAmount: number;
    onLimitedUseSet?: (usedAmount: number) => void;
    onCustomizationsRemove?: () => void;
    proficiencyBonus: number;
    theme?: CharacterTheme;
}
export default class ActionDetail extends React.PureComponent<Props> {
    static defaultProps = {
        showCustomize: true,
        largePoolMinAmount: 11,
    };

    handleRemoveCustomizations = () => {
        const { onCustomizationsRemove } = this.props;

        if (onCustomizationsRemove) {
            onCustomizationsRemove();
        }
    };

    renderSmallAmountSlotPool = (): React.ReactNode => {
        const { ruleData, abilityLookup, action, onLimitedUseSet, proficiencyBonus } = this.props;

        let limitedUse = ActionUtils.getLimitedUse(action);
        if (!limitedUse) {
            return null;
        }

        const numberUsed = LimitedUseUtils.getNumberUsed(limitedUse);
        const maxUses = LimitedUseUtils.deriveMaxUses(limitedUse, abilityLookup, ruleData, proficiencyBonus);

        return <SlotManager used={numberUsed} available={maxUses} size={'small'} onSet={onLimitedUseSet} />;
    };

    renderLargeAmountSlotPool = (): React.ReactNode => {
        const { ruleData, abilityLookup, action, isReadonly, onLimitedUseSet, proficiencyBonus } = this.props;

        let limitedUse = ActionUtils.getLimitedUse(action);
        if (!limitedUse) {
            return null;
        }

        const numberUsed = LimitedUseUtils.getNumberUsed(limitedUse);
        const maxUses = LimitedUseUtils.deriveMaxUses(limitedUse, abilityLookup, ruleData, proficiencyBonus);

        return (
            <SlotManagerLarge available={maxUses} used={numberUsed} onSet={onLimitedUseSet} isReadonly={isReadonly} />
        );
    };

    renderLimitedUses = (): React.ReactNode => {
        const { action, largePoolMinAmount, abilityLookup, ruleData, proficiencyBonus } = this.props;

        let limitedUse = ActionUtils.getLimitedUse(action);
        if (!limitedUse) {
            return null;
        }

        let maxUses = LimitedUseUtils.deriveMaxUses(limitedUse, abilityLookup, ruleData, proficiencyBonus);
        if (!maxUses) {
            return null;
        }
        let numberUsed = LimitedUseUtils.getNumberUsed(limitedUse);
        let totalSlots: number = Math.max(maxUses, numberUsed);

        return (
            <div className="ct-action-detail__limited-uses">
                <div className="ct-action-detail__limited-uses-label">Limited Use</div>
                <div className="ct-action-detail__limited-uses-manager">
                    {totalSlots >= largePoolMinAmount
                        ? this.renderLargeAmountSlotPool()
                        : this.renderSmallAmountSlotPool()}
                </div>
            </div>
        );
    };

    renderCustomize = (): React.ReactNode => {
        const { action, onCustomDataUpdate, showCustomize, entityValueLookup, ruleData, isReadonly } = this.props;

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);

        if (!showCustomize || isReadonly || mappingId === null || mappingEntityTypeId === null) {
            return null;
        }

        let valueEditorComponents: Array<Constants.AdjustmentTypeEnum> = [
            Constants.AdjustmentTypeEnum.TO_HIT_OVERRIDE,
            Constants.AdjustmentTypeEnum.TO_HIT_BONUS,
            Constants.AdjustmentTypeEnum.FIXED_VALUE_BONUS,
            Constants.AdjustmentTypeEnum.DISPLAY_AS_ATTACK,
            Constants.AdjustmentTypeEnum.NAME_OVERRIDE,
            Constants.AdjustmentTypeEnum.NOTES,
        ];

        const isCustomized = ActionUtils.isCustomized(action);

        return (
            <Collapsible
                layoutType={'minimal'}
                header={`Customize${isCustomized ? '*' : ''}`}
                className="ct-action-detail__customize"
            >
                <EditorBox>
                    <ValueEditor
                        dataLookup={ValueUtils.getEntityData(entityValueLookup, mappingId, mappingEntityTypeId)}
                        onDataUpdate={onCustomDataUpdate}
                        valueEditors={valueEditorComponents}
                        ruleData={ruleData}
                        labelOverrides={{
                            [Constants.AdjustmentTypeEnum.NAME_OVERRIDE]: 'Name',
                            [Constants.AdjustmentTypeEnum.FIXED_VALUE_BONUS]: 'Damage Bonus',
                        }}
                        defaultValues={{
                            [Constants.AdjustmentTypeEnum.DISPLAY_AS_ATTACK]:
                                ActionUtils.isDefaultDisplayAsAttack(action),
                        }}
                        layoutType={'compact'}
                    />
                    <RemoveButton
                        enableConfirm={true}
                        size="medium"
                        style="filled"
                        disabled={!isCustomized}
                        isInteractive={isCustomized}
                        onClick={this.handleRemoveCustomizations}
                    >
                        {isCustomized ? 'Remove' : 'No'} Customizations
                    </RemoveButton>
                </EditorBox>
            </Collapsible>
        );
    };

    getRangedInfoData = (): Array<React.ReactNode> => {
        const { action, ruleData, theme } = this.props;

        let rangeInfo = ActionUtils.getRange(action);
        let attackRangeId = ActionUtils.getAttackRangeId(action);

        let rangeAreas: Array<React.ReactNode> = [];
        if (rangeInfo !== null && attackRangeId === Constants.AttackTypeRangeEnum.RANGED) {
            if (rangeInfo.origin && rangeInfo.origin !== Constants.SpellRangeTypeEnum.RANGED) {
                let spellRangeType = RuleDataUtils.getSpellRangeType(rangeInfo.origin, ruleData);
                if (spellRangeType !== null) {
                    rangeAreas.push(spellRangeType.name);
                }
            }

            if (rangeInfo.range) {
                rangeAreas.push(
                    <React.Fragment>
                        <DistanceNumber number={rangeInfo.range} isDarkMode={theme?.isDarkMode} />
                        {rangeInfo.longRange && (
                            <span className="ct-action-detail__range-long">({rangeInfo.longRange})</span>
                        )}
                    </React.Fragment>,
                );
            }

            if (rangeInfo.aoeSize) {
                let aoeType: ModelInfoContract | null = null;
                if (rangeInfo.aoeType !== null) {
                    aoeType = RuleDataUtils.getAoeType(rangeInfo.aoeType, ruleData);
                }
                rangeAreas.push(
                    <React.Fragment>
                        <DistanceNumber number={rangeInfo.aoeSize} isDarkMode={theme?.isDarkMode} />
                        {aoeType !== null && (
                            <span className="ct-action-detail__range-shape">
                                <AoeTypeIcon
                                    className="ct-action-detail__range-icon"
                                    type={FormatUtils.slugify(aoeType.name) as ComponentConstants.AoeTypePropType}
                                    themeMode={theme?.isDarkMode ? 'gray' : 'dark'}
                                />
                            </span>
                        )}
                    </React.Fragment>,
                );
            }
        }

        return rangeAreas;
        //`
    };

    renderDamageProperties = (): React.ReactNode => {
        const { action, theme } = this.props;

        let damage = ActionUtils.getDamage(action);

        let damageDisplay: React.ReactNode = null;
        if (damage.value !== null) {
            if (typeof damage.value === 'number') {
                damageDisplay = damage.value;
            } else {
                damageDisplay = DiceUtils.renderDice(damage.value);
            }
        }

        return (
            <React.Fragment>
                {damageDisplay !== null && <PropertyListItem label="Damage">{damageDisplay}</PropertyListItem>}
                {damage.type !== null && damage.type.name !== null && (
                    <PropertyListItem label="Damage Type">
                        <DamageTypeIcon
                            theme={theme}
                            type={FormatUtils.slugify(damage.type.name) as ComponentConstants.DamageTypePropType}
                        />
                        {damage.type.name}
                    </PropertyListItem>
                )}
            </React.Fragment>
        );
    };

    renderWeaponProperties = (): React.ReactNode => {
        const { action, ruleData, theme } = this.props;

        let isProficient = ActionUtils.isProficient(action);
        let toHit = ActionUtils.getToHit(action);
        let attackRangeId = ActionUtils.getAttackRangeId(action);
        let statId = ActionUtils.getStatId(action);
        let requiresAttackRoll = ActionUtils.requiresAttackRoll(action);
        let requiresSavingThrow = ActionUtils.requiresSavingThrow(action);
        let activation = ActionUtils.getActivation(action);
        let notes = ActionUtils.getNotes(action);
        let attackSubtypeId = ActionUtils.getAttackSubtypeId(action);
        let saveStateId = ActionUtils.getSaveStatId(action);

        let rangeAreas: Array<React.ReactNode> = [];
        if (attackRangeId === Constants.AttackTypeRangeEnum.RANGED) {
            rangeAreas = this.getRangedInfoData();
        } else {
            rangeAreas.push(
                <React.Fragment>
                    <DistanceNumber number={ActionUtils.getReach(action)} isDarkMode={theme?.isDarkMode} /> Reach
                </React.Fragment>,
            );
        }

        return (
            <PropertyList className="ct-action-detail__properties">
                {activation !== null && activation.activationType && (
                    <PropertyListItem label={'Action Type'}>
                        {ActivationUtils.renderActivation(activation, ruleData)}
                    </PropertyListItem>
                )}
                {attackSubtypeId && (
                    <PropertyListItem label="Attack Type">{ActionUtils.getAttackSubtypeName(action)}</PropertyListItem>
                )}
                {requiresAttackRoll && (
                    <PropertyListItem label="To Hit">
                        {toHit === null ? '--' : <SignedNumber number={toHit} />}
                    </PropertyListItem>
                )}
                {requiresSavingThrow && (
                    <PropertyListItem label="Attack/Save">
                        {saveStateId !== null ? RuleDataUtils.getStatNameById(saveStateId, ruleData) : ''}{' '}
                        {ActionUtils.getAttackSaveValue(action)}
                    </PropertyListItem>
                )}
                {this.renderDamageProperties()}
                {requiresAttackRoll && (
                    <PropertyListItem label="Stat">
                        {statId === null ? '--' : RuleDataUtils.getStatNameById(statId, ruleData)}
                    </PropertyListItem>
                )}
                {rangeAreas.length > 0 && (
                    <PropertyListItem label="Range/Area">
                        {rangeAreas.map((node, idx) => (
                            <React.Fragment key={idx}>
                                {node}
                                {idx + 1 < rangeAreas.length ? '/' : ''}
                            </React.Fragment>
                        ))}
                    </PropertyListItem>
                )}
                {isProficient && <PropertyListItem label="Proficient">Yes</PropertyListItem>}
                {notes && <PropertyListItem label="Notes">{notes}</PropertyListItem>}
            </PropertyList>
        );
    };

    renderSpellProperties = (): React.ReactNode => {
        const { action, ruleData } = this.props;

        let isProficient = ActionUtils.isProficient(action);
        let toHit = ActionUtils.getToHit(action);
        let statId = ActionUtils.getStatId(action);
        let requiresAttackRoll = ActionUtils.requiresAttackRoll(action);
        let requiresSavingThrow = ActionUtils.requiresSavingThrow(action);
        let activation = ActionUtils.getActivation(action);
        let notes = ActionUtils.getNotes(action);
        let attackSubtypeId = ActionUtils.getAttackSubtypeId(action);
        let saveStateId = ActionUtils.getSaveStatId(action);

        let rangeAreas = this.getRangedInfoData();
        return (
            <PropertyList className="ct-action-detail__properties">
                {activation !== null && activation.activationType && (
                    <PropertyListItem label={'Casting Time'}>
                        {ActivationUtils.renderActivation(activation, ruleData)}
                    </PropertyListItem>
                )}
                {attackSubtypeId && (
                    <PropertyListItem label="Attack Type">{ActionUtils.getAttackSubtypeName(action)}</PropertyListItem>
                )}
                {requiresAttackRoll && (
                    <PropertyListItem label="To Hit">
                        {toHit === null ? '--' : <SignedNumber number={toHit} />}
                    </PropertyListItem>
                )}
                {requiresSavingThrow && (
                    <PropertyListItem label="Attack/Save">
                        {saveStateId !== null ? RuleDataUtils.getStatNameById(saveStateId, ruleData) : ''}{' '}
                        {ActionUtils.getAttackSaveValue(action)}
                    </PropertyListItem>
                )}
                {this.renderDamageProperties()}
                {requiresAttackRoll && (
                    <PropertyListItem label="Stat">
                        {statId === null ? '--' : RuleDataUtils.getStatNameById(statId, ruleData)}
                    </PropertyListItem>
                )}
                {rangeAreas.length > 0 && (
                    <PropertyListItem label="Range/Area">
                        {rangeAreas.map((node, idx) => (
                            <React.Fragment key={idx}>
                                {node}
                                {idx + 1 < rangeAreas.length ? '/' : ''}
                            </React.Fragment>
                        ))}
                    </PropertyListItem>
                )}
                {isProficient && <PropertyListItem label="Proficient">Yes</PropertyListItem>}
                {notes && <PropertyListItem label="Notes">{notes}</PropertyListItem>}
            </PropertyList>
        );
    };

    renderGeneralProperties = (): React.ReactNode => {
        const { action, ruleData, theme } = this.props;

        let isProficient = ActionUtils.isProficient(action);
        let toHit = ActionUtils.getToHit(action);
        let statId = ActionUtils.getStatId(action);
        let requiresAttackRoll = ActionUtils.requiresAttackRoll(action);
        let requiresSavingThrow = ActionUtils.requiresSavingThrow(action);
        let activation = ActionUtils.getActivation(action);
        let notes = ActionUtils.getNotes(action);
        let attackRangeId = ActionUtils.getAttackRangeId(action);
        let saveStateId = ActionUtils.getSaveStatId(action);

        let rangeAreas: Array<React.ReactNode> = [];
        if (attackRangeId === Constants.AttackTypeRangeEnum.RANGED) {
            rangeAreas = this.getRangedInfoData();
        } else {
            rangeAreas.push(
                <React.Fragment>
                    <DistanceNumber number={ActionUtils.getReach(action)} isDarkMode={theme?.isDarkMode} /> Reach
                </React.Fragment>,
            );
        }

        return (
            <PropertyList className="ct-action-detail__properties">
                {activation !== null && activation.activationType && (
                    <PropertyListItem label={'Action Type'}>
                        {ActivationUtils.renderActivation(activation, ruleData)}
                    </PropertyListItem>
                )}
                {requiresAttackRoll && (
                    <PropertyListItem label="To Hit">
                        {toHit === null ? '--' : <SignedNumber number={toHit} />}
                    </PropertyListItem>
                )}
                {requiresSavingThrow && (
                    <PropertyListItem label="Attack/Save">
                        {saveStateId !== null ? RuleDataUtils.getStatNameById(saveStateId, ruleData) : ''}{' '}
                        {ActionUtils.getAttackSaveValue(action)}
                    </PropertyListItem>
                )}
                {this.renderDamageProperties()}
                {requiresAttackRoll && (
                    <PropertyListItem label="Stat">
                        {statId === null ? '--' : RuleDataUtils.getStatNameById(statId, ruleData)}
                    </PropertyListItem>
                )}
                {rangeAreas.length > 0 && (
                    <PropertyListItem label="Range/Area">
                        {rangeAreas.map((node, idx) => (
                            <React.Fragment key={idx}>
                                {node}
                                {idx + 1 < rangeAreas.length ? '/' : ''}
                            </React.Fragment>
                        ))}
                    </PropertyListItem>
                )}
                {isProficient && <PropertyListItem label="Proficient">Yes</PropertyListItem>}
                {notes && <PropertyListItem label="Notes">{notes}</PropertyListItem>}
            </PropertyList>
        );
    };

    renderProperties = (): React.ReactNode => {
        const { action } = this.props;

        const actionTypeId = ActionUtils.getActionTypeId(action);
        switch (actionTypeId) {
            case Constants.ActionTypeEnum.SPELL:
                return this.renderSpellProperties();
            case Constants.ActionTypeEnum.WEAPON:
                return this.renderWeaponProperties();
            case Constants.ActionTypeEnum.GENERAL:
                return this.renderGeneralProperties();
            default:
            // not implemented
        }

        return null;
    };

    renderDescription = (): React.ReactNode => {
        const { action, ruleData, inventoryLookup } = this.props;

        let description = ActionUtils.getDescription(action);
        let dataOrigin = ActionUtils.getDataOrigin(action);
        let dataOriginType = ActionUtils.getDataOriginType(action);

        if (!description) {
            description = EntityUtils.getPrimaryDescription(dataOrigin);
        }

        if (dataOriginType === Constants.DataOriginTypeEnum.ITEM) {
            const itemContract = dataOrigin.primary as BaseInventoryContract;
            const itemMappingId = ItemUtils.getMappingId(itemContract);
            const item = HelperUtils.lookupDataOrFallback(inventoryLookup, itemMappingId);

            if (item === null) {
                return null;
            }

            let infusion = ItemUtils.getInfusion(item);

            if (infusion && !AccessUtils.isAccessible(InfusionUtils.getAccessType(infusion))) {
                const sources = InfusionUtils.getSources(infusion);

                let sourceNames: Array<string> = [];
                if (sources.length > 0) {
                    sources.forEach((sourceMapping) => {
                        let source = RuleDataUtils.getSourceDataInfo(sourceMapping.sourceId, ruleData);

                        if (source !== null && source.description !== null) {
                            sourceNames.push(source.description);
                        }
                    });
                }

                const sourceName: string | null =
                    sourceNames.length > 0 ? FormatUtils.renderNonOxfordCommaList(sourceNames) : null;

                return (
                    <div className="ct-action-detail__marketplace-cta">
                        <MarketplaceCta
                            showImage={false}
                            sourceName={sourceName}
                            description={'To unlock this infusion, check out the Marketplace to view purchase options.'}
                        />
                    </div>
                );
            }
        }

        if (!description) {
            return null;
        }

        return <HtmlContent className="ct-action-detail__description" html={description} />;
    };

    render() {
        return (
            <div className="ct-action-detail">
                {this.renderLimitedUses()}
                {this.renderCustomize()}
                {this.renderProperties()}
                {this.renderDescription()}
            </div>
        );
    }
}
